import { CONNECTOR_ENDPOINT_FORMAT, CONNECTOR_ENDPOINT_MODE } from "@sagot/constants";
import { usePermissions } from "../../../../session/permissions";
import type { WebhookEndpoint } from "../types";
import { useEndpoints } from "../use-workflows";
import type { ConnectorAuth } from "./connector-api-types";

export interface ConnectorEndpoint {
  id: string;
  name: string;
  enabled: boolean;
  /** The header the endpoint sends with every request, when it has connector authentication. */
  auth: ConnectorAuth | null;
}

/** The endpoints a connector request can call: set to "Only from workflows" and the Sagot format. */
export function connectorEndpoints(endpoints: readonly WebhookEndpoint[]): ConnectorEndpoint[] {
  return endpoints
    .filter((endpoint) => endpoint.delivery_mode === CONNECTOR_ENDPOINT_MODE && endpoint.format === CONNECTOR_ENDPOINT_FORMAT)
    .map((endpoint) => ({
      id: endpoint.id,
      name: endpoint.description ?? endpoint.url,
      enabled: endpoint.enabled,
      auth: endpoint.connector_auth,
    }));
}

export interface ConnectorEndpointsState {
  endpoints: ConnectorEndpoint[];
  loading: boolean;
  failed: boolean;
  /** Seeing the endpoints needs the permission to manage webhooks. */
  readable: boolean;
  retry: () => void;
}

/** The endpoint list of the Data connector form (shares the query of the other step forms). */
export function useConnectorEndpoints(): ConnectorEndpointsState {
  const permissions = usePermissions();
  const readable = permissions.can("webhooks.manage");
  const query = useEndpoints(readable);
  return {
    endpoints: connectorEndpoints(query.data ?? []),
    loading: permissions.isPending || (readable && query.isPending),
    failed: readable && query.isError,
    readable: readable || permissions.isPending,
    retry: () => void query.refetch(),
  };
}
